import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzIcon, BringzzText, BringzzDropdown } from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const SubstanceProducts = () => {
    const { t } = useTranslation();
    const { backPress, navigate } = useNavigation();
    const { sendRequest } = useRequest();
    const { substanceId } = useParams();
    const [searchParams] = useSearchParams();
    const substanceName = searchParams.get('name') || 'Substance';

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [sortBy, setSortBy] = useState('name_asc');

    const observer = useRef();
    const isLoadingMore = useRef(false);

    const sortOptions = [
        { value: 'name_asc', label: t('component.search.sort.name_asc') },
        { value: 'name_desc', label: t('component.search.sort.name_desc') },
        { value: 'price_asc', label: t('component.search.sort.price_asc') },
        { value: 'price_desc', label: t('component.search.sort.price_desc') },
        { value: 'pzn', label: 'PZN' },
    ];

    const lastProductElementRef = useCallback(node => {
        if (loading || isLoadingMore.current) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !isLoadingMore.current) {
                isLoadingMore.current = true;
                setPage(prevPage => prevPage + 1);
            }
        }, {
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const fetchProducts = async (pageNum = 1, reset = false) => {
        try {
            setLoading(true);
            isLoadingMore.current = true;

            const response = await sendRequest({
                method: 'POST',
                url: '/module/productsubstance/getProductsBySubstance',
                data: {
                    substanceId: parseInt(substanceId),
                    page: pageNum,
                    limit: 20,
                    sortBy: sortBy,
                    sortOrder: 'ASC'
                }
            });

            if (response?.status === 'success' && response?.data) {
                const newProducts = response.data;

                if (reset || pageNum === 1) {
                    setProducts(newProducts);
                } else {
                    setProducts(prev => [...prev, ...newProducts]);
                }

                setHasMore(newProducts.length === 20);
            }
        } catch (error) {
            console.error('Error fetching products by substance:', error);
            toast.error(t('component.search.general.error'));
        } finally {
            setLoading(false);
            isLoadingMore.current = false;
        }
    };

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        fetchProducts(1, true);
    }, [sortBy, substanceId]);

    useEffect(() => {
        if (page > 1) {
            fetchProducts(page);
        }
    }, [page]);

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return null;
        return parseFloat(price).toFixed(2);
    };

    return (
        <BringzzPageContainer
            className="bg-sand pb-32"
            authenticated={false}
            footerHover={true}
            header={{
                left: <BringzzIcon
                    size="22"
                    icon="IconChevronLeft"
                    folder="LineIcons"
                    className="cursor-pointer"
                    onClick={backPress}
                />,
                title: substanceName
            }}
        >
            <div className='pt-2'>
                <div className='rounded-xl bg-white mx-2 py-2'>
                    {/* Sort Dropdown */}
                    <div className="p-4 border-b">
                        <BringzzDropdown
                            options={sortOptions}
                            value={sortBy}
                            onChange={handleSortChange}
                            placeholder={t('component.search.general.sort_by')}
                        />
                    </div>

                    {/* Products List */}
                    {products.length > 0 ? (
                        products.map((product, index) => (
                            <div
                                key={product.id || index}
                                className="p-4 border-b flex items-center justify-between cursor-pointer"
                                ref={index === products.length - 1 ? lastProductElementRef : null}
                                onClick={() => navigate(`/product/details/${product.integrationId}`)}
                            >
                                <div className="flex items-center gap-3 flex-grow min-w-0">
                                    {product.productImage && (
                                        <img
                                            src={product.productImage}
                                            alt={product.name}
                                            className="w-12 h-12 object-contain rounded flex-shrink-0"
                                        />
                                    )}
                                    <div className="min-w-0 flex-grow">
                                        <BringzzText tag='h4' className="truncate">
                                            {product.name || `PZN: ${product.pzn}`}
                                        </BringzzText>
                                        <BringzzText tag='p' className="text-sm text-gray-500 truncate">
                                            {product.companyName || ''}
                                            {product.packSize ? ` \u2022 ${product.packSize} ${product.packSizeUnit || ''}` : ''}
                                        </BringzzText>
                                        {formatPrice(product.price) && (
                                            <BringzzText tag='p' className="text-sm font-semibold mt-0.5">
                                                {formatPrice(product.price)} \u20AC
                                            </BringzzText>
                                        )}
                                    </div>
                                </div>
                                <BringzzIcon
                                    size="20"
                                    icon="IconChevronRight"
                                    folder="LineIcons"
                                    className="text-gray-400 flex-shrink-0 ml-2"
                                />
                            </div>
                        ))
                    ) : loading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : (
                        <div className="p-4 text-center">
                            <BringzzText tag='p'>{t('component.search.empty.no_products')}</BringzzText>
                        </div>
                    )}

                    {loading && products.length > 0 && (
                        <div className="flex items-center justify-center p-4">
                            <BringzzIcon
                                size="24"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    )}
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default SubstanceProducts;
